import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { company: "", name: "", phone: "", email: "", comment: "" };

export default function RequestForm() {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!values.company.trim()) e.company = "Укажите название компании";
    if (!values.name.trim()) e.name = "Укажите имя";
    if (!values.phone.trim() || values.phone.replace(/\D/g, "").length < 5)
      e.phone = "Укажите телефон";
    if (!values.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      e.email = "Укажите корректный email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Пожалуйста, заполните обязательные поля");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/requests`, values);
      setSubmitted(true);
      setValues(initial);
      toast.success("Заявка отправлена. Мы свяжемся с вами.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Не удалось отправить заявку. Попробуйте позже."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="request"
      data-testid="request-section"
      className="section relative bg-[#F8FAFC]"
    >
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-6">Заявка</div>
            <h2 className="font-display text-[#0A192F] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1] tracking-tight">
              Оставьте заявку — подберём позиции и&nbsp;пришлём КП
            </h2>
            <p className="mt-6 text-slate-600 text-base lg:text-lg leading-relaxed">
              Опишите задачу или приложите спецификацию — мы свяжемся с вами в рабочее время
              и подготовим коммерческое предложение.
            </p>

            <div className="mt-10 space-y-5">
              {[
                { label: "Ответ", value: "В рабочее время" },
                { label: "Расчёт", value: "Индивидуально по ТЗ" },
                { label: "География", value: "Вся Россия" },
              ].map((r) => (
                <div
                  key={r.label}
                  className="flex items-center justify-between border-b border-slate-200 pb-4"
                >
                  <span className="text-sm uppercase tracking-[0.2em] text-slate-500">
                    {r.label}
                  </span>
                  <span className="font-display text-[#0A192F] font-semibold">
                    {r.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 relative">
            <div className="glass-card p-6 lg:p-10 relative overflow-hidden">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="py-8 text-center"
                    data-testid="request-success"
                  >
                    <div className="w-16 h-16 mx-auto bg-[#FF6B00] flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="mt-6 font-display text-2xl lg:text-3xl text-[#0A192F] font-semibold">
                      Заявка отправлена
                    </h3>
                    <p className="mt-3 text-slate-600 max-w-md mx-auto">
                      Мы получили ваше обращение и свяжемся с вами в ближайшее рабочее время
                      для уточнения деталей и подготовки коммерческого предложения.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      data-testid="request-submit-another"
                      className="mt-8 inline-flex items-center gap-2 text-[#0A192F] font-medium hover:text-[#FF6B00] transition-colors"
                    >
                      Отправить ещё одну заявку
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={onSubmit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-5"
                    data-testid="request-form"
                    noValidate
                  >
                    <Field
                      id="company"
                      label="Компания *"
                      value={values.company}
                      onChange={update("company")}
                      error={errors.company}
                      placeholder="ООО «Название»"
                    />
                    <Field
                      id="name"
                      label="Имя *"
                      value={values.name}
                      onChange={update("name")}
                      error={errors.name}
                      placeholder="Как к вам обращаться"
                    />
                    <Field
                      id="phone"
                      label="Телефон *"
                      type="tel"
                      value={values.phone}
                      onChange={update("phone")}
                      error={errors.phone}
                      placeholder="+7 ___ ___ __ __"
                    />
                    <Field
                      id="email"
                      label="Email *"
                      type="email"
                      value={values.email}
                      onChange={update("email")}
                      error={errors.email}
                      placeholder="you@company.ru"
                    />
                    <div className="md:col-span-2">
                      <label
                        htmlFor="comment"
                        className="text-xs uppercase tracking-[0.16em] font-medium text-slate-500"
                      >
                        Комментарий
                      </label>
                      <textarea
                        id="comment"
                        data-testid="input-comment"
                        rows={4}
                        value={values.comment}
                        onChange={update("comment")}
                        placeholder="Опишите задачу, приложите список позиций или спецификацию"
                        className="mt-2 w-full bg-white border border-slate-200 focus:border-[#0A192F] px-4 py-3.5 text-base text-slate-900 placeholder:text-slate-400 transition-colors resize-none"
                      />
                    </div>

                    <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="request-submit-btn"
                        className="inline-flex items-center justify-center gap-3 bg-[#FF6B00] hover:bg-[#E66000] disabled:opacity-70 text-white px-8 py-4 font-medium transition-colors"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Отправка...
                          </>
                        ) : (
                          <>
                            Отправить заявку
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                      <p className="text-xs text-slate-500 max-w-sm">
                        Отправляя заявку, вы соглашаетесь на обработку персональных данных
                        в целях подготовки коммерческого предложения.
                      </p>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, value, onChange, error, placeholder, type = "text" }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="text-xs uppercase tracking-[0.16em] font-medium text-slate-500"
      >
        {label}
      </label>
      <input
        id={id}
        data-testid={`input-${id}`}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`mt-2 w-full bg-white border ${
          error ? "border-red-400" : "border-slate-200 focus:border-[#0A192F]"
        } px-4 py-3.5 text-base text-slate-900 placeholder:text-slate-400 transition-colors`}
      />
      {error && (
        <div
          data-testid={`error-${id}`}
          className="mt-1.5 text-xs text-red-500"
        >
          {error}
        </div>
      )}
    </div>
  );
}
